import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    AppDispatch,
    loginV2,
    setJwt,
    initializeAccountDetail,
} from "@redux";
import { authApi, cartApi } from "@api";
import { IAuthLogin, ICustomerDetail } from "@interfaces";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { lsJwt, lsEmail } from "src/Utilities/storage";
import { PATH_FORGOT_PASSWORD } from "@routes";
import { sessionHelper } from "@utilities";
import {
    getPrimaryTravellerEmail as getPrimaryEmail,
    generateRandomPassword,
    determineGuestSignupOutcome,
    canProceedWithoutSignup,
    validateGuestLoginInput,
    determineLoginOutcome,
} from "./guestCheckoutHelpers";

interface UseGuestCheckoutProps {
    sessionId?: string;
    customers: ICustomerDetail[];
    isLoggedIn: boolean;
    onSessionConverted?: () => Promise<void> | void;
}

interface PendingOrderArgs {
    paymentType: string;
    pointsApplied: number;
    commissionApplied?: number | null;
    overrideSessionId?: string;
}

export const useGuestCheckout = ({
    sessionId,
    customers,
    isLoggedIn,
    onSessionConverted,
}: UseGuestCheckoutProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [showGuestLoginModal, setShowGuestLoginModal] = useState(false);
    const [guestLoginEmail, setGuestLoginEmail] = useState("");
    const [guestLoginPassword, setGuestLoginPassword] = useState("");
    const [showGuestLoginPassword, setShowGuestLoginPassword] = useState(false);
    const [isGuestLoggingIn, setIsGuestLoggingIn] = useState(false);
    const [pendingOrderArgs, setPendingOrderArgs] = useState<PendingOrderArgs | null>(null);
    const [pendingTravellerCallback, setPendingTravellerCallback] = useState<{
        onComplete: () => Promise<void>;
        onCancel: () => void;
    } | null>(null);

    const getPrimaryTravellerEmail = useCallback((): string => {
        return getPrimaryEmail(customers);
    }, [customers]);

    const autoCreateGuestAccount = async (email: string): Promise<boolean> => {
        const randomPassword = generateRandomPassword();
        try {
            const signUpRes = await authApi.signUp({
                email,
                password: randomPassword,
                skip_otp: true,
                is_password_randomly_generated: true,
            });
            if (signUpRes.data.code === 200) {
                const loginRes = (await dispatch(
                    loginV2({ email, password: randomPassword }),
                )) as { payload: IAuthLogin };

                if (loginRes.payload.data?.accessToken) {
                    sessionStorage.setItem("guestCheckoutInProgress", "true");

                    lsJwt.setItem(loginRes.payload.data);
                    lsEmail.setItem(email);

                    if (sessionId) {
                        try {
                            await cartApi.mergeSessionCart(sessionId, loginRes.payload.data.accessToken);
                            sessionHelper.clearSession();
                            sessionHelper.clearBookNowSession();
                            if (onSessionConverted) {
                                await onSessionConverted();
                            }
                        } catch (mergeError) {
                            console.error("Failed to merge session cart:", mergeError);
                        }
                    }

                    dispatch(setJwt(loginRes.payload.data));
                    await dispatch(initializeAccountDetail()).unwrap();

                    return true;
                }
            }
            return false;
        } catch {
            return false;
        }
    };

    const ensureGuestAccount = async (
        onAccountReady: () => Promise<void>,
        orderArgs: PendingOrderArgs,
        setIsCreating: (v: boolean) => void,
    ) => {
        if (canProceedWithoutSignup(isLoggedIn)) {
            await onAccountReady();
            return;
        }

        const email = getPrimaryTravellerEmail();
        if (!email) {
            ErrorToast({ Message: "Please add a traveller with an email address before proceeding" });
            return;
        }

        setIsCreating(true);
        try {
            const accountCreated = await autoCreateGuestAccount(email);
            const outcome = determineGuestSignupOutcome(accountCreated, email);
            if (outcome.success) {
                await onAccountReady();
            } else if (outcome.showLoginModal) {
                setPendingOrderArgs(orderArgs);
                setGuestLoginEmail(outcome.email);
                setGuestLoginPassword("");
                setShowGuestLoginModal(true);
                setIsCreating(false);
            }
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "Failed to create account. Please try again.",
            });
            setIsCreating(false);
        }
    };

    const handleGuestLogin = async (onLoginSuccess?: () => Promise<void>) => {
        const validation = validateGuestLoginInput(guestLoginEmail, guestLoginPassword);
        if (!validation.valid) {
            ErrorToast({ Message: validation.error! });
            return;
        }

        setIsGuestLoggingIn(true);
        try {
            const loginRes = (await dispatch(
                loginV2({ email: guestLoginEmail, password: guestLoginPassword }),
            )) as { payload: IAuthLogin };

            const loginOutcome = determineLoginOutcome(!!loginRes.payload.data?.accessToken);
            if (!loginOutcome.success) {
                ErrorToast({ Message: loginOutcome.error! });
                return;
            }

            lsJwt.setItem(loginRes.payload.data);
            lsEmail.setItem(guestLoginEmail);

            if (sessionId) {
                try {
                    await cartApi.mergeSessionCart(sessionId, loginRes.payload.data.accessToken);
                    sessionHelper.clearSession();
                    sessionHelper.clearBookNowSession();
                    if (onSessionConverted) {
                        await onSessionConverted();
                    }
                } catch (mergeError) {
                    console.error("Failed to merge session cart:", mergeError);
                }
            }

            dispatch(setJwt(loginRes.payload.data));
            await dispatch(initializeAccountDetail()).unwrap();

            setShowGuestLoginModal(false);
            setGuestLoginPassword("");

            if (onLoginSuccess) {
                await onLoginSuccess();
            }
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "Login failed. Please try again.",
            });
        } finally {
            setIsGuestLoggingIn(false);
        }
    };

    const tryAutoLoginOnTravellerAdd = async (
        _email: string,
        onComplete: () => Promise<void>,
    ): Promise<void> => {
        await onComplete();
    };

    const handleForgotPassword = () => {
        if (!guestLoginEmail) return;
        setShowGuestLoginModal(false);
        setPendingOrderArgs(null);
        if (pendingTravellerCallback) {
            pendingTravellerCallback.onCancel();
            setPendingTravellerCallback(null);
        }
        navigate(PATH_FORGOT_PASSWORD);
    };

    const closeGuestLoginModal = () => {
        setShowGuestLoginModal(false);
        setPendingOrderArgs(null);
        if (pendingTravellerCallback) {
            pendingTravellerCallback.onCancel();
            setPendingTravellerCallback(null);
        }
    };

    return {
        showGuestLoginModal,
        guestLoginEmail,
        guestLoginPassword,
        setGuestLoginPassword,
        showGuestLoginPassword,
        setShowGuestLoginPassword,
        isGuestLoggingIn,
        pendingOrderArgs,
        pendingTravellerCallback,
        ensureGuestAccount,
        handleGuestLogin,
        closeGuestLoginModal,
        tryAutoLoginOnTravellerAdd,
        handleForgotPassword,
    };
};
